import React, { useState, useEffect } from 'react';
import FileUpload from '../../components/FileUpload';
import type { Track } from '../../services/trackTypes';
import { useTranslation } from 'react-i18next';
import type { TrackPermissions } from '../../types/track';

import toast from 'react-hot-toast';
import { resolveTrackMediaUrl } from '../../services/mediaAccess';
import { uploadArtworkAsync, deleteArtwork } from '../../services/trackService';
import { useWebSocketNotifications } from '../../hooks/useWebSocketNotifications';
import { logInfo } from '../../services/logtail';
import CassetteLoader from '../../components/CassetteLoader';
import { useAuth } from '../../contexts/AuthContext';
import ImageLightbox from '../../components/ImageLightbox';
import { classNames } from '../../utils/classNames';


interface TrackHeaderProps {
  artwork: string | null;
  setArtwork: (artwork: string | null) => void;
  showArtworkUpload: boolean;
  setShowArtworkUpload: (show: boolean) => void;
  Track: Track;
  setTrack: (track: Track | null) => void;
  testArtworkBlobUrl?: string; // test-only prop
  permissions?: TrackPermissions;
  size?: 'default' | 'large';
}





const TrackHeader: React.FC<TrackHeaderProps> = ({ setArtwork, showArtworkUpload, setShowArtworkUpload, Track, setTrack, testArtworkBlobUrl, permissions, size = 'default' }) => {
  const { t } = useTranslation();
  const { unlinkGoogleDrive } = useAuth();




  const [artworkBlobUrl, setArtworkBlobUrl] = useState<string | null>(null);
  const [artworkFile, setArtworkFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [artworkModal, setArtworkModal] = useState(false);

  // Use WebSocket notifications for artwork upload progress
  useWebSocketNotifications({
    trackId: Track.id,
    fileType: 'artwork', // Only process artwork upload notifications
    onUploadComplete: (notification) => {
      if (notification.file_type === 'artwork' && notification.status === 'completed') {
        logInfo('Artwork upload completed via WebSocket', { 
          trackId: Track.id, 
          fileId: notification.file_id,
          fileName: notification.filename 
        });
        
        // Clear uploading state
        setUploading(false);
        
        // OPTIMIZATION: Update track state with new artwork info immediately
        // This ensures the remove button works with the actual backend file ID
        
        if (notification.file_id) {
          // Update the track state with the new artwork information from backend
          const newArtwork = {
            fileId: notification.file_id,
            name: notification.filename || 'artwork',
            url: '' // The existing useEffect will fetch the actual image from backend
          };
          
          // Update parent's track state with the new artwork
          const updatedTrack = { ...Track, artwork: newArtwork };
          setTrack(updatedTrack);
          
          // If we have the local file, create a temporary blob URL for immediate display
          if (artworkFile) {
            const tempBlobUrl = URL.createObjectURL(artworkFile);
            setArtworkBlobUrl(tempBlobUrl);
            
            // Clean up the temporary file state
            setArtworkFile(null);
            setShowArtworkUpload(false);
            
            // Clean up the temporary blob URL after a delay to allow for backend sync
            setTimeout(() => {
              URL.revokeObjectURL(tempBlobUrl);
              // The track state now has the real artwork info, so remove button will work
            }, 5000);
          } else {
            // No local file, just clear the upload state
            setShowArtworkUpload(false);
          }
        }
      }
    },
    onUploadFailed: (notification) => {
      if (notification.file_type === 'artwork') {
        logInfo('Artwork upload failed via WebSocket', { 
          trackId: Track.id, 
          fileName: notification.filename,
          error: notification.error 
        });
        setUploading(false);
      }
    },
    onAuthIssue: (notification) => {
      setUploading(false);
      setShowArtworkUpload(false);
      logInfo('Auth issue in TrackHeader - upload cancelled', { notification, trackId: Track.id });
    },
    unlinkGoogleDrive
  });



  useEffect(() => {
    let revoke: (() => void) | undefined;
    if (Track.artwork && Track.artwork.fileId) {
      (async () => {
        try {
          const fileId = Track.artwork?.fileId ?? '';
          if (!fileId) { setArtworkBlobUrl(null); return; }
          const access = await resolveTrackMediaUrl(Track.id, fileId, 'artwork', Track.storageProvider);
          setArtworkBlobUrl(access.url);
          revoke = access.revoke;
        } catch {
          setArtworkBlobUrl('placeholder');
        }
      })();
    } else {
      setArtworkBlobUrl(null);
    }
    return () => {
      revoke?.();
    };
  }, [Track.artwork, Track.id, Track.storageProvider]);





  const handleArtworkUpload = async () => {
    if (!artworkFile) return;
    setUploading(true);
    try {
      // Start the async upload - it will complete via WebSocket
      await uploadArtworkAsync(Track.id, artworkFile);
      // Completion toast is shown once via WebSocket (deduped globally).
      
      // Fallback: if WebSocket doesn't arrive within 30 seconds, clear loading state
      setTimeout(() => {
        if (uploading) {
          setUploading(false);
        }
      }, 30000);
    } catch (e) {
      toast.error('Upload failed.');
      setUploading(false);
    }
    // Note: uploading state will be cleared when WebSocket notification arrives
  };

  const handleRemoveArtwork = async () => {
    setUploading(true);
    try {
      if (Track.artwork && Track.artwork.fileId) {
        try {
          await deleteArtwork(Track.id, Track.artwork.fileId);
        } catch (error) {
          // If deletion fails (e.g., file already deleted from Google Drive), 
          // we still want to remove it from the UI
          console.warn('Failed to delete artwork from backend:', error);
        }
      }
      
      // OPTIMIZATION: Update parent's track state immediately without backend refresh
      // Clear all local artwork state
      setArtworkBlobUrl(null);
      setArtworkFile(null);
      setShowArtworkUpload(false);
      
      // Update the parent's track state to remove artwork
      // This ensures the UI stays in sync with the backend
      const updatedTrack = { ...Track, artwork: undefined };
      setTrack(updatedTrack);
      
      // Show success message
      toast.success('Artwork removed successfully!');
      
    } catch (error) {
      console.error('Error removing artwork:', error);
      // Even if deletion fails, clear the local state for better UX
      setArtworkBlobUrl(null);
      setArtworkFile(null);
      setShowArtworkUpload(false);
      
      // Update parent state even on error for consistent UX
      const updatedTrack = { ...Track, artwork: undefined };
      setTrack(updatedTrack);
      
      toast.error('Failed to remove artwork from backend, but cleared from display');
    } finally {
      setUploading(false);
    }
  };

  const artworkUrl = testArtworkBlobUrl !== undefined ? testArtworkBlobUrl : artworkBlobUrl;
  const showArtworkFrame =
    Boolean(artworkUrl && artworkUrl !== 'placeholder') ||
    Boolean(artworkFile) ||
    artworkUrl === 'placeholder' ||
    uploading ||
    Boolean(!artworkUrl && Track.artwork);
  const artworkSquareClass = size === 'large' ? 'w-full max-w-[20rem] aspect-square' : 'w-full max-w-[12rem] aspect-square';

  return (
    <div className="flex items-start justify-center w-full">
      <div
        className={classNames(
          'relative group flex flex-col items-center w-full mx-auto',
          showArtworkFrame && 'rounded-xl transition-shadow p-5 border',
          showArtworkFrame && (size === 'large' ? 'max-w-sm' : 'max-w-xs'),
        )}
        style={showArtworkFrame ? { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' } : undefined}
      >
        {/* Full-size loader/skeleton when uploading or loading image - sized to match artwork for better LCP */}
        {(uploading || (!artworkUrl && Track.artwork)) && (
          <div 
            className={`${size === 'large' ? 'w-72 h-72 sm:w-80 sm:h-80' : 'w-48 h-48'} rounded-xl flex items-center justify-center shadow-lg border-2 animate-pulse`}
            style={{ 
              borderColor: 'var(--color-border)',
              background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-hover) 100%)'
            }}
            data-testid="artwork-skeleton"
          >
            <CassetteLoader size={64} variant="inline" />
          </div>
        )}
        {artworkUrl && artworkUrl !== 'placeholder' ? (
          <>
            <img
              src={artworkUrl}
              alt="Artwork preview"
              className={`object-cover rounded-xl shadow-lg border-2 cursor-pointer hover:scale-105 transition-transform duration-200 ${size === 'large' ? 'w-72 h-72 sm:w-80 sm:h-80' : 'w-48 h-48'}`}
              style={{ borderColor: 'var(--color-border)' }}
              onClick={() => setArtworkModal(true)}
              data-testid="artwork-image"
            />
            {permissions?.canEditMetadata && (
              <div className="flex gap-2 mt-2 items-center justify-center w-full">
                <button
                  className="px-3 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: 'var(--color-hover)', color: 'var(--color-text)' }}
                  onClick={handleRemoveArtwork}
                  type="button"
                >
                  {t('buttons.remove')}
                </button>
              </div>
            )}
            {artworkModal && (
              <ImageLightbox
                src={artworkUrl}
                alt="Artwork large preview"
                onClose={() => setArtworkModal(false)}
              />
            )}
          </>
        ) : artworkFile ? (
          <>
            <img src={URL.createObjectURL(artworkFile)} alt="Preview" className={`${size === 'large' ? 'w-72 h-72 sm:w-80 sm:h-80' : 'w-48 h-48'} object-cover rounded-lg shadow-lg border`} style={{ borderColor: 'var(--color-border)' }} />
            <div className="flex gap-2 mt-2 items-center justify-center w-full">
              <button
                className="px-3 py-1 text-white rounded text-xs font-medium" style={{ backgroundColor: 'var(--brand-from)' }}
                onClick={handleArtworkUpload}
                disabled={uploading}
                data-testid="artwork-upload-button"
              >
                {t('buttons.upload')}
              </button>
              <button
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs font-medium"
                onClick={() => { setArtworkFile(null); setShowArtworkUpload(false); }}
                disabled={uploading}
              >
                {t('buttons.cancel')}
              </button>
            </div>
          </>
        ) : artworkUrl === 'placeholder' ? (
          <>
            <div className={`${size === 'large' ? 'w-72 h-72 sm:w-80 sm:h-80' : 'w-48 h-48'} rounded-xl flex items-center justify-center shadow-lg border-2`} style={{ 
              borderColor: 'var(--color-border)',
              backgroundColor: '#f8f9fa',
              backgroundImage: `
                radial-gradient(circle at 20% 20%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
                linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
              `
            }}>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-600">Artwork Ready</p>
              </div>
            </div>
            {permissions?.canEditMetadata && (
              <div className="flex gap-2 mt-2 items-center justify-center w-full">
                <button
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs font-medium"
                  onClick={handleRemoveArtwork}
                  type="button"
                >
                  {t('buttons.remove')}
                </button>
              </div>
            )}
          </>
        ) : (
          <FileUpload
            className={artworkSquareClass}
            fill
            showPreview={false}
            label={t('promo.uploadArtwork')}
            fileCategory="image"
            onFile={files => {
              const file = Array.isArray(files) ? files[0] : files?.[0];
              if (file) setArtworkFile(file);
            }}
            multiple={false}
            data-testid="artwork-file-input"
          />
        )}
        {showArtworkUpload && (
          <div className="absolute inset-0 flex items-center justify-center rounded z-10" style={{ backgroundColor: 'var(--overlay-bg-90)' }}>
            <FileUpload
              label={t('promo.uploadArtwork')}
              fileCategory="image"
              onFile={files => {
                const file = Array.isArray(files) ? files[0] : files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = e => {
                    setArtwork(e.target?.result as string);
                    setShowArtworkUpload(false);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              multiple={false}
            />
            <button
              type="button"
              className="absolute top-1 right-1 text-xl font-bold rounded-full px-2 py-0.5"
              style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
              onClick={() => setShowArtworkUpload(false)}
              aria-label="Cancel artwork upload"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackHeader; 